import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Search, Grid3X3, List } from 'lucide-react';
import { useMsal } from "@azure/msal-react";
import { cn } from '../lib/utils';
import { loginRequest } from "../authConfig";
import { useNavigate } from 'react-router-dom';
import { setRxApps } from 'redux/appCenterSlice';

const API_URL = process.env.REACT_APP_API_URL || "";
const arrow = "/arrows.svg"


const AppGallery = () => {

  interface App {
    name: string;
    url: string;
    imageURL?: string;
    description?: string;
  }

  interface AppData {
    webApps: App[];
    desktopApps: App[];
    powerBIReports: App[];
  }
  const { instance, accounts } = useMsal();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [apps, setApps] = useState<AppData>({
    webApps: [],
    desktopApps: [],
    powerBIReports: []
  });

  useEffect(() => {
    const fetchApps = async () => {
      try {
        setIsLoading(true);
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0], 
        });

        const accessToken = response.accessToken;
  
        const apiResponse = await fetch(API_URL + '/engineering/appcenter', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
  
        if (!apiResponse.ok) {
          throw new Error(`HTTP error! Status: ${apiResponse.status}`);
        }
  
        const data = await apiResponse.json();
        setApps(data);
        dispatch(setRxApps(data));
        setError(null);
      } catch (err) {
        console.error('Error fetching apps:', err);
        setError('Failed to load applications. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchApps();
  }, [accounts, instance]);
  

  // Filter applications based on search
  const filterApps = (apps: App[]) => {
    return apps.filter(app => {
      const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  };

  const filteredWebApps = filterApps(apps.webApps || []);
  const filteredDesktopApps = filterApps(apps.desktopApps || []);
  const filteredPowerBIReports = filterApps(apps.powerBIReports || []);

  // Handle app click - navigate to details page with app name
  const handleAppClick = (appName: string) => {
    navigate(`/details/${encodeURIComponent(appName)}`);
  };

  const defaultIcon = "/mediaslot.svg";
  const powerBIIcon = "/fabriclogo.svg";

  const renderAppCard = (app: App, isWebApp: boolean, isPowerBIReport: boolean = false) => {
    return (
      <div 
        key={app.name} 
        className={cn(
          "group transition-all duration-300 hover:shadow-md cursor-pointer tool-card",
          viewMode === 'list' && "flex items-center h-16"
        )}
        onClick={() => handleAppClick(app.name)}
      >
         <div className="tool-description">
          <strong>{app.name}</strong><br />
          {app.description}
         </div>

         {viewMode === 'grid' ? (
         <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
         {/* Background image */}
         <img 
          src="/effectBackground.svg" 
          alt="Card background" 
          className="absolute w-[200%] h-[200%] top-[-50%] left-[-27%] opacity-100 pointer-events-none"
          style={{ objectFit: 'contain' }}
        />
         
          {/* Grid layout */}
          <div className="relative grid grid-cols-12 gap-2 h-full">
           {/* Icon area - 3 columns */}
           <div className="col-span-5 flex items-center justify-center">
             <div className="w-15 h-15 flex items-center justify-center ml-4 mt-1">
               <img
                 src={app.imageURL || (isPowerBIReport ? powerBIIcon : defaultIcon)} 
                 alt={`${app.name} icon`} 
                 className="w-full h-full object-contain"
                 onError={(e) => {
                   // Fallback a la imagen por defecto si hay un error
                   const target = e.target as HTMLImageElement;
                   target.onerror = null;
                   target.src = isPowerBIReport ? powerBIIcon : defaultIcon;
                 }}
               />
             </div>
           </div>
           
           {/* Text area - 9 columns */}
           <div className="col-span-7 flex flex-col justify-center pr-8">
             <h3 className="font-bold text-black text-sm leading-tight text-left">{app.name}</h3>
           </div>
         </div>
         
         {/* Arrow positioned at top-right */}
         <div className="absolute top-2 right-2">
           <img
             src={arrow} 
             alt="Arrow icon" 
             className="w-4 h-4"
           />
         </div>
       </div>
        ) : (
          <div className="flex items-center w-full bg-white border border-gray-200 rounded-lg p-3">
            <div className={cn(
              "h-10 w-10 shrink-0 flex items-center justify-center rounded-md",
              isWebApp ? "bg-blue-50" : "bg-gray-100"
            )}>
              <img
                src={app.imageURL || (isPowerBIReport ? powerBIIcon : defaultIcon)}
                alt={`${app.name} icon`}
                className="h-6 w-6 object-contain"
                onError={(e) => {
                  // Fallback a una div coloreada si hay un error
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `<div class="${cn(
                    "h-4 w-4 rounded-sm",
                    isWebApp ? "bg-blue-500" : "bg-gray-700"
                  )}"></div>`;
                }}
              />
            </div>
            <div className="ml-3">
              <h3 className="font-medium text-gray-800 text-sm">{app.name}</h3>
              <p className="text-xs text-gray-500 truncate">
                {isWebApp ? app.url : "Download"}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="">
      
      {/* Header with category - Increased size */}
      <div className="relative rounded-lg overflow-hidden mb-8">
        <img src="/header.svg" alt="Header background" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 p-16 pl-16 text-left">
          <h2 className="text-5xl font-roboto  text-white text-left">App Center</h2>
          <p className="text-white text-base mt-3 max-w-xl text-left">
          Browse and access Daikin web and desktop applications.
          </p>
        </div>
      </div>

      {/* Search and filters row */}
      <div className="flex flex-col lg:flex-row items-center gap-4 mb-6">
        {/* Search bar - matching Figma reference */}
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search"
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        </div>

        {/* Filter button - moved outside search bar */}
        <button className="flex items-center justify-center bg-[#1D5176] text-white p-2 rounded h-9 w-9">
          <svg width="24" height="24" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_235_687" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="25">
              <rect y="24.4851" width="24" height="24" transform="rotate(-90 0 24.4851)" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_235_687)">
              <path d="M19.4807 16.4851L18.2672 17.6929L15.173 14.5986L15.173 21.9851L13.673 21.9851L13.673 14.5986L10.5788 17.6929L9.3655 16.4851L14.673 11.1774L19.4807 16.4851ZM14.1155 8.48511L8.80775 13.7929L4 8.48511L5.2135 7.27736L8.30775 10.3716L8.30775 2.98511L9.80775 2.98511L9.80775 10.3716L12.902 7.27736L14.1155 8.48511Z" fill="#F1F7FD"/>
            </g>
          </svg>
        </button>
        
        {/* View mode toggles */}
        <div className="flex items-center gap-1 ml-auto">
          <button 
            className={cn(
              "p-1.5 rounded border transition-colors", 
              viewMode === 'grid' ? "bg-[#1D5176] text-white border-blue-800" : "bg-white text-gray-700 border-gray-300"
            )}
            onClick={() => setViewMode('grid')}
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button 
            className={cn(
              "p-1.5 rounded border transition-colors",
              viewMode === 'list' 
                ? "bg-[#1D5176] text-white border-blue-800 disabled:opacity-50 disabled:cursor-not-allowed" 
                : "bg-white text-gray-700 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            )} 
            disabled={true} 
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>


      {/* Loading and error states */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Loading applications...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Web Applications Section */}
      {!isLoading && !error && (
        <>
          {filteredWebApps.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Web Applications</h3>
              <div className={cn(
                viewMode === 'grid' 
                  ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" 
                  : "flex flex-col gap-3"
              )}>
                {filteredWebApps.map((app) => renderAppCard(app, true))}
              </div>
            </div>
          )}

          {/* Desktop Applications Section */}
          {filteredDesktopApps.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Desktop Applications</h3>
              <div className={cn(
                viewMode === 'grid' 
                  ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" 
                  : "flex flex-col gap-3"
              )}>
                {filteredDesktopApps.map((app) => renderAppCard(app, false))}
              </div>
            </div>
          )}

          {/* Power BI Reports Section */}
          {filteredPowerBIReports.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Power BI & Reporting</h3>
              <div className={cn(
                viewMode === 'grid' 
                  ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" 
                  : "flex flex-col gap-3"
              )}>
                {filteredPowerBIReports.map((app) => renderAppCard(app, false, true))}
              </div>
            </div>
          )}

          {/* No results message */}
          {filteredWebApps.length === 0 && filteredDesktopApps.length === 0 && filteredPowerBIReports.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">No applications found matching your search.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AppGallery;